/**
 * Canonical path-changing redirects shared by middleware and revenue attribution.
 *
 * This file is URL disposition config. A change here is judged on its merits
 * by the SEO policy's quality rules (one-hop redirect to a real equivalent,
 * aligned canonicals, sitemaps and internal links). There is no change freeze:
 * the owner lifted the 2026-09-28 disposition freeze on 2026-10-25 and ruled
 * that none may return (Notion SEO policy, Rule 17;
 * https://app.notion.com/p/3f8ee6ef264e8100b807f19ee5dd74fb).
 */
export const LEGACY_CRAWL_CANONICAL_PATHS: Readonly<Record<string, string>> = {
  // Same-day tools already live on the hubs; keep one search destination.
  "/panchang/today/": "/panchang/",
  "/shubh-muhurat/today/": "/shubh-muhurat/",
  "/hi/shubh-muhurat/today/": "/hi/shubh-muhurat/",
  "/free-astrology-chat/": "/ai-astrologer/",
  "/membership/": "/ai-astrologer/",
  "/matching/": "/kundli-matching/",
  // These pages already named these winners in their canonical metadata.
  // Resolve the same consolidation in routing and sitemap ownership.
  "/manglik-calculator/": "/mangal-dosha-calculator/",
  "/saturn-transit/": "/shani-transit/",
  // `vivah` was never a governed muhurat event id (the source-of-truth slug is
  // `marriage`) and the generic dynamic fallback earned 0 GSC rows/90d as of
  // 2026-08-04. Consolidate the orphan instead of grandfathering it into the
  // fail-closed dynamic-index registry.
  "/muhurat/vivah/": "/muhurat/marriage/",
  // Consolidated 2026-07-22: thin /prashna/ guide → the /prashna-kundali/ tool
  // (kundli-matching-style cannibalization; the tool page dominates the query).
  "/prashna/": "/prashna-kundali/",
  // `/vastu/` is a stale static artifact. The current hub, locale pair, room
  // directory, schema, and sitemap authority all live under `/vastu-shastra/`.
  "/vastu/": "/vastu-shastra/",
  "/kundali/": "/free-kundali/",
  "/daily-rashifal/": "/rashifal/",
  "/today-rashifal/": "/rashifal/",
  // Owner decision 2026-10-26: since the sign pages merged into it (#2838) the
  // /rashifal/ hub server-renders the same all-12 engine section, so the two
  // were rendered twins (5-word-shingle Jaccard 0.596 against the ADR-0104
  // 0.60 ceiling) splitting one intent (GSC 28d: 59 vs 51 impressions).
  "/daily-horoscope/": "/rashifal/",
  // Friends feature retired 2026-10-26 (vedic_friends and vedic_invite_links
  // never held a row; 0 /api/v1/social/* requests in 30 days). Its job was
  // "see how your chart aligns with a friend's": /compatibility/ computes that
  // for any two people, while /kundli-matching/ is the marriage-specific match.
  "/friends/": "/compatibility/",
  "/friends/invite/": "/compatibility/",
  "/pages/about/": "/about/",
  "/pages/about-us/": "/about/",
  "/pages/contact/": "/contact/",
  "/pages/contact-us/": "/contact/",
  // Moved from the account-level Cloudflare Bulk Redirect list (deleted
  // 2026-10-01 so this map is the only redirect source). Same targets as live.
  "/pages/track-order/": "/contact/",
  "/wpm/": "/",
  "/policies/privacy-policy/": "/privacy/",
  "/policies/refund-policy/": "/refund-policy/",
  "/policies/shipping-policy/": "/shipping-policy/",
  "/policies/terms-of-service/": "/terms/",
  "/remedies/": "/upay/",
  "/hi/remedies/": "/hi/upay/",
  // `/dosha/` is a D1 content row (category hub, served 200, self-canonical)
  // that duplicates the app's /doshas/ hub for the same query. #2818 repointed
  // every internal link to /doshas/; this consolidates the URL itself, so the
  // row leaves the sitemap and the served-noindex seed by disposition.
  "/dosha/": "/doshas/",
  "/dosha/kaal-sarp-dosha/": "/kaal-sarp-dosha/",
  "/dosha/mangal-dosha/": "/mangal-dosha-calculator/",
  "/mangal-dosha/": "/mangal-dosha-calculator/",
  "/dosha/pitra-dosha/": "/pitra-dosha/",
  "/dosha/shani-dosha/": "/sade-sati/",
  "/dosha/rahu-dosha/": "/upay/rahu/",
  "/remedies/for-dosha/rahu_dosha/": "/upay/rahu/",
  "/dosha/ketu-dosha/": "/upay/ketu/",
  "/remedies/for-dosha/ketu_dosha/": "/upay/ketu/",
  "/hi/monthly-rashifal/": "/hi/aaj-ka-rashifal/",
  "/hi/muhurat/naamkaran/": "/hi/muhurat/",
  // Retired physical-product pages (Shopify catalog era, GSC 404 backlog 2026-07-24).
  // Only the karungali handles have an equivalent page. The other retired
  // products, the collections and the journal blog answer 410 since the
  // 2026-09-30 cleanup release (`shopify-leftover` in crawl-policy-data.json):
  // a 301 to the home page or /upay/ is a soft 404.
  //
  // FIXED 2026-08-09: all three pointed at
  // `/blogs/news/karungali-mala-benefits-...`, which does NOT exist and returns
  // 404. A live URL redirecting into a 404 is a soft-404 signal, and it wasted
  // whatever equity these paths still carried — on a domain under an active
  // algorithmic classification (ADR-0091) that is the opposite of what a redirect
  // is for. The page that actually survives is the editorial guide, verified 200.
  //
  // REPAIRED AGAIN 2026-08-09 (ADR-0092): `/products/karungali-mala/` became the
  // live product route, and the two legacy duplicate handles 301'd into it.
  //
  // RETIRED 2026-10-26 (ADR-0122 closeout): the product was drafted in Shopify and
  // the `/products/[handle]` route was removed, so all three handles consolidate
  // into the editorial guide again, the one karungali page that survives.
  "/products/karungali-mala/": "/blog/karungali-mala-guide/",
  "/products/karungali-mala-1/": "/blog/karungali-mala-guide/",
  "/products/karungali-mala-siddh-rudraksha-bracelet-combo/": "/blog/karungali-mala-guide/",
  // GSC 404 audit 2026-10-29: the two Shopify karungali articles still drew
  // impressions (435 and 301 before the July drop) and answered 404, the second
  // being the dead target the 2026-08-09 note above describes. The guide covers
  // both: its sections "Benefits people seek from it", "How to identify an
  // original Karungali mala" and the "Is Karungali the same as ebony?" FAQ.
  "/blogs/news/how-to-identify-original-karungali-mala-7-tests-that-actually-work/":
    "/blog/karungali-mala-guide/",
  "/blogs/news/karungali-mala-benefits-what-tradition-and-science-say-about-black-ebony/":
    "/blog/karungali-mala-guide/",
  "/mahadasha/saturn/": "/mahadasha/shani/",
  "/blogs/news/baby-names-by-nakshatra-complete-guide-for-2026/": "/baby-names/",
  "/blogs/news/sade-sati-2026-which-rashis-are-affected-and-what-to-do/": "/sade-sati/",
  "/blogs/news/free-kundali-how-ai-is-making-vedic-astrology-accessible-t/": "/free-kundali/",
  "/blogs/news/free-kundali-how-ai-is-making-vedic-astrology-accessible-to-everyone/":
    "/free-kundali/",
  "/blogs/news/saturn-transit-in-aries-2026-effects-on-all-12-rashis/": "/shani-transit/",
  "/blogs/news/how-to-find-your-moon-sign-rashi-by-date-of-birth/": "/rashi-calculator/",
  "/blogs/news/kaal-sarp-dosha-signs-effects-and-proven-remedies/": "/kaal-sarp-dosha/",
  "/blogs/news/kundali-matching-beyond-36-gun-milan-what-really-matters/": "/kundli-matching/",
  "/blogs/news/kundli-matching-for-marriage-gun-milan-explained/": "/kundli-matching/",
  "/blogs/news/mangal-dosha-meaning-effects-and-remedies/": "/mangal-dosha-calculator/",
  "/blogs/news/what-is-kundli-complete-guide-to-vedic-birth-chart/": "/free-kundali/",
  "/blogs/news/nakshatra-the-27-lunar-mansions-of-vedic-astrology/": "/nakshatra/",
  "/pages/terms-and-conditions/": "/terms/",
  "/pages/privacy-policy/": "/privacy/",
  "/pages/delete-account/": "/privacy/",
  "/jupiter-transit-2026/": "/jupiter-transit/",
  "/rahu-transit-2026/": "/rahu-transit/",
  "/hi/jupiter-transit-2026/": "/hi/transits/",
  "/hi/rahu-transit-2026/": "/hi/transits/",
  "/kundali-milan/": "/kundli-matching/",
  "/kundli-milan/": "/kundli-matching/",
  "/hi/kundali-milan/": "/hi/kundli-matching/",
  "/hi/kundli-milan/": "/hi/kundli-matching/",
  "/lal-kitab-remedies/": "/lal-kitab/",
  // Explain retirement (owner decision 2026-09-28, cleanup release 2026-09-30):
  // an /explain/ page 301s only to a page carrying the same content; every other
  // /explain/ URL answers 410 (crawl-policy-data.json `explain*` gone rules). The
  // rashi, nakshatra and bhava explainers are pattern rules there.
  "/explain/dosha/guru-chandal-dosh/": "/guru-chandal-dosha/",
  "/explain/dosha/kaal-sarp-dosh/": "/kaal-sarp-dosha/",
  "/explain/dosha/kemadruma-yoga/": "/kemdrum-yoga/",
  "/explain/dosha/mangal-dosh/": "/mangal-dosha-calculator/",
  "/explain/dosha/pitra-dosh/": "/pitra-dosha/",
  "/explain/yoga/dhana-yoga/": "/dhana-yoga-calculator/",
  "/explain/yoga/kala-sarpa-yoga/": "/kaal-sarp-dosha/",
  "/explain/yoga/raja-yoga/": "/raj-yoga-calculator/",
  "/explain/koota/nadi/": "/nadi-dosha/",
  "/hi/explain/koota/nadi/": "/hi/nadi-dosh/",
  // The five rebuilt yoga guides (YOGA_GUIDES[].explainPath, pinned by
  // canonical-redirects.test.ts) replace their retired explainers one-to-one.
  "/explain/yoga/gajakesari-yoga/": "/yoga/gajakesari/",
  "/explain/yoga/budha-aditya-yoga/": "/yoga/budhaditya/",
  "/explain/yoga/malavya-yoga/": "/yoga/malavya/",
  "/explain/yoga/vipreet-raja-yoga/": "/yoga/viparita-raja-yoga/",
  "/explain/yoga/neecha-bhanga-raja-yoga/": "/yoga/neecha-bhanga-raja-yoga/",
  // One URL per paid report (cleanup release 2026-09-30): each report-intent
  // landing sold the same product as its /reports/<plan>/ page under a second
  // self-canonical URL (five pairs shared a title). The landing 301s to the
  // product page its own `reportSlug` names (pinned by canonical-redirects.test.ts).
  "/annual-forecast/": "/reports/annual-forecast/",
  "/baby-name-report/": "/reports/baby-name/",
  "/career-report/": "/reports/career-report/",
  "/career-astrology-report/": "/reports/career-report/",
  "/dosha-report/": "/reports/dosha-remedy-plan/",
  "/dosha-remedy-plan/": "/reports/dosha-remedy-plan/",
  "/gemstone-report/": "/reports/gemstone-recommendation/",
  "/gochar-report/": "/reports/gochar-transit/",
  "/lal-kitab-report/": "/reports/lal-kitab/",
  "/mahadasha-forecast/": "/reports/mahadasha-forecast/",
  "/marriage-report/": "/reports/marriage-report/",
  "/muhurat-report/": "/reports/muhurat-report/",
  "/numerology-report/": "/reports/numerology-life/",
  "/pitra-dosh-report/": "/reports/pitra-dosh/",
  "/sade-sati-report/": "/reports/sade-sati/",
  // The two Hindi dosha landings sold the same Dosha Remedy Plan; the one whose
  // slug matches the product keeps the Hindi URL.
  "/hi/dosha-report/": "/hi/dosha-remedy-plan/",
  // Keyword-variant landings of the one AI-chat product (5-gram Jaccard 0.36,
  // 0 clicks in the 28 days before the release). Their Hindi pages stay and
  // declare /ai-astrologer/ as their English alternate (src/i18n/locales.ts).
  "/chat-with-astrologer/": "/ai-astrologer/",
  "/talk-to-astrologer/": "/ai-astrologer/",
  // Owner-approved 2026-10-08 (true one-to-one equivalents, under a recorded
  // disposition override). The Hindi twins of the two pages above fold into the
  // Hindi half of the /ai-astrologer/ pair; their routes are deleted.
  "/hi/jyotishi-se-chat/": "/hi/muft-jyotish-chat/",
  "/hi/jyotishi-se-baat-karen/": "/hi/muft-jyotish-chat/",
  // Addresses people type that answered 404: the common "kundli" spelling of
  // the tool and a dropped "i" in the Hindi prefix. The hyphenated festival
  // ids (/calendar/festival/karwa-chauth/ and the rest, en and hi) are the
  // generated block at the end of this map.
  "/free-kundli/": "/free-kundali/",
  "/h/prashna-kundali/": "/hi/prashna-kundali/",
  // Wave 2 merged each ekadashi guide into its festival page; the pairs are
  // EKADASHI_GUIDE_REDIRECTS (src/lib/ekadashi-data.ts), pinned by the test.
  "/ekadashi/nirjala-ekadashi/": "/calendar/festival/nirjala_ekadashi/",
  "/ekadashi/devshayani-ekadashi/": "/calendar/festival/devshayani_ekadashi/",
  "/ekadashi/devuthani-ekadashi/": "/calendar/festival/devuthani_ekadashi/",
  "/ekadashi/mokshada-ekadashi/": "/calendar/festival/mokshada_ekadashi/",
  "/ekadashi/papankusha-ekadashi/": "/calendar/festival/papankusha_ekadashi/",
  "/ekadashi/amalaki-ekadashi/": "/calendar/festival/amalaki_ekadashi/",
  "/ekadashi/varuthini-ekadashi/": "/calendar/festival/varuthini_ekadashi/",
  "/ekadashi/kamada-ekadashi/": "/calendar/festival/kamada_ekadashi/",
  // One sitemap entry point and one feed (cleanup release 2026-09-30). The
  // content Worker applies this map before serving any sitemap route.
  "/sitemap.xml": "/sitemap-index.xml",
  "/sitemap-content.xml": "/sitemap-index.xml",
  "/jyotish-sitemap.xml": "/sitemap-index.xml",
  "/rss.xml": "/feed.xml",
  // A year-suffixed festival id whose evergreen twin carries every year's date.
  "/calendar/festival/makar_sankranti_2027/": "/calendar/festival/makar_sankranti/",
  "/hi/calendar/festival/makar_sankranti_2027/": "/hi/calendar/festival/makar_sankranti/",
  // BEGIN generated by scripts/generate-festival-hyphen-redirects.mjs (do not edit by hand).
  // Owner decision 2026-10-09: the hyphen spelling of each promoted festival id
  // is a one-to-one equivalent of its underscore page (en and hi).
  "/calendar/festival/makar-sankranti/": "/calendar/festival/makar_sankranti/",
  "/hi/calendar/festival/makar-sankranti/": "/hi/calendar/festival/makar_sankranti/",
  "/calendar/festival/vasant-panchami/": "/calendar/festival/vasant_panchami/",
  "/hi/calendar/festival/vasant-panchami/": "/hi/calendar/festival/vasant_panchami/",
  "/calendar/festival/maha-shivaratri/": "/calendar/festival/maha_shivaratri/",
  "/hi/calendar/festival/maha-shivaratri/": "/hi/calendar/festival/maha_shivaratri/",
  "/calendar/festival/holika-dahan/": "/calendar/festival/holika_dahan/",
  "/hi/calendar/festival/holika-dahan/": "/hi/calendar/festival/holika_dahan/",
  "/calendar/festival/chaitra-navratri-start/": "/calendar/festival/chaitra_navratri_start/",
  "/hi/calendar/festival/chaitra-navratri-start/": "/hi/calendar/festival/chaitra_navratri_start/",
  "/calendar/festival/ram-navami/": "/calendar/festival/ram_navami/",
  "/hi/calendar/festival/ram-navami/": "/hi/calendar/festival/ram_navami/",
  "/calendar/festival/hanuman-jayanti/": "/calendar/festival/hanuman_jayanti/",
  "/hi/calendar/festival/hanuman-jayanti/": "/hi/calendar/festival/hanuman_jayanti/",
  "/calendar/festival/akshaya-tritiya/": "/calendar/festival/akshaya_tritiya/",
  "/hi/calendar/festival/akshaya-tritiya/": "/hi/calendar/festival/akshaya_tritiya/",
  "/calendar/festival/buddha-purnima/": "/calendar/festival/buddha_purnima/",
  "/hi/calendar/festival/buddha-purnima/": "/hi/calendar/festival/buddha_purnima/",
  "/calendar/festival/guru-purnima/": "/calendar/festival/guru_purnima/",
  "/hi/calendar/festival/guru-purnima/": "/hi/calendar/festival/guru_purnima/",
  "/calendar/festival/raksha-bandhan/": "/calendar/festival/raksha_bandhan/",
  "/hi/calendar/festival/raksha-bandhan/": "/hi/calendar/festival/raksha_bandhan/",
  "/calendar/festival/ganesh-chaturthi/": "/calendar/festival/ganesh_chaturthi/",
  "/hi/calendar/festival/ganesh-chaturthi/": "/hi/calendar/festival/ganesh_chaturthi/",
  "/calendar/festival/anant-chaturdashi/": "/calendar/festival/anant_chaturdashi/",
  "/hi/calendar/festival/anant-chaturdashi/": "/hi/calendar/festival/anant_chaturdashi/",
  "/calendar/festival/pitru-paksha-start/": "/calendar/festival/pitru_paksha_start/",
  "/hi/calendar/festival/pitru-paksha-start/": "/hi/calendar/festival/pitru_paksha_start/",
  "/calendar/festival/mahalaya-amavasya/": "/calendar/festival/mahalaya_amavasya/",
  "/hi/calendar/festival/mahalaya-amavasya/": "/hi/calendar/festival/mahalaya_amavasya/",
  "/calendar/festival/sharad-navratri-start/": "/calendar/festival/sharad_navratri_start/",
  "/hi/calendar/festival/sharad-navratri-start/": "/hi/calendar/festival/sharad_navratri_start/",
  "/calendar/festival/karwa-chauth/": "/calendar/festival/karwa_chauth/",
  "/hi/calendar/festival/karwa-chauth/": "/hi/calendar/festival/karwa_chauth/",
  "/calendar/festival/narak-chaturdashi/": "/calendar/festival/narak_chaturdashi/",
  "/hi/calendar/festival/narak-chaturdashi/": "/hi/calendar/festival/narak_chaturdashi/",
  "/calendar/festival/govardhan-puja/": "/calendar/festival/govardhan_puja/",
  "/hi/calendar/festival/govardhan-puja/": "/hi/calendar/festival/govardhan_puja/",
  "/calendar/festival/bhai-dooj/": "/calendar/festival/bhai_dooj/",
  "/hi/calendar/festival/bhai-dooj/": "/hi/calendar/festival/bhai_dooj/",
  "/calendar/festival/chhath-puja/": "/calendar/festival/chhath_puja/",
  "/hi/calendar/festival/chhath-puja/": "/hi/calendar/festival/chhath_puja/",
  "/calendar/festival/dev-diwali/": "/calendar/festival/dev_diwali/",
  "/hi/calendar/festival/dev-diwali/": "/hi/calendar/festival/dev_diwali/",
  "/calendar/festival/mauni-amavasya/": "/calendar/festival/mauni_amavasya/",
  "/hi/calendar/festival/mauni-amavasya/": "/hi/calendar/festival/mauni_amavasya/",
  "/calendar/festival/magha-gupt-navratri-start/": "/calendar/festival/magha_gupt_navratri_start/",
  "/hi/calendar/festival/magha-gupt-navratri-start/":
    "/hi/calendar/festival/magha_gupt_navratri_start/",
  "/calendar/festival/ashadha-gupt-navratri-start/":
    "/calendar/festival/ashadha_gupt_navratri_start/",
  "/hi/calendar/festival/ashadha-gupt-navratri-start/":
    "/hi/calendar/festival/ashadha_gupt_navratri_start/",
  "/calendar/festival/pausha-putrada-ekadashi/": "/calendar/festival/pausha_putrada_ekadashi/",
  "/hi/calendar/festival/pausha-putrada-ekadashi/":
    "/hi/calendar/festival/pausha_putrada_ekadashi/",
  "/calendar/festival/shattila-ekadashi/": "/calendar/festival/shattila_ekadashi/",
  "/hi/calendar/festival/shattila-ekadashi/": "/hi/calendar/festival/shattila_ekadashi/",
  "/calendar/festival/jaya-ekadashi/": "/calendar/festival/jaya_ekadashi/",
  "/hi/calendar/festival/jaya-ekadashi/": "/hi/calendar/festival/jaya_ekadashi/",
  "/calendar/festival/vijaya-ekadashi/": "/calendar/festival/vijaya_ekadashi/",
  "/hi/calendar/festival/vijaya-ekadashi/": "/hi/calendar/festival/vijaya_ekadashi/",
  "/calendar/festival/amalaki-ekadashi/": "/calendar/festival/amalaki_ekadashi/",
  "/hi/calendar/festival/amalaki-ekadashi/": "/hi/calendar/festival/amalaki_ekadashi/",
  "/calendar/festival/papmochani-ekadashi/": "/calendar/festival/papmochani_ekadashi/",
  "/hi/calendar/festival/papmochani-ekadashi/": "/hi/calendar/festival/papmochani_ekadashi/",
  "/calendar/festival/kamada-ekadashi/": "/calendar/festival/kamada_ekadashi/",
  "/hi/calendar/festival/kamada-ekadashi/": "/hi/calendar/festival/kamada_ekadashi/",
  "/calendar/festival/varuthini-ekadashi/": "/calendar/festival/varuthini_ekadashi/",
  "/hi/calendar/festival/varuthini-ekadashi/": "/hi/calendar/festival/varuthini_ekadashi/",
  "/calendar/festival/mohini-ekadashi/": "/calendar/festival/mohini_ekadashi/",
  "/hi/calendar/festival/mohini-ekadashi/": "/hi/calendar/festival/mohini_ekadashi/",
  "/calendar/festival/apara-ekadashi/": "/calendar/festival/apara_ekadashi/",
  "/hi/calendar/festival/apara-ekadashi/": "/hi/calendar/festival/apara_ekadashi/",
  "/calendar/festival/nirjala-ekadashi/": "/calendar/festival/nirjala_ekadashi/",
  "/hi/calendar/festival/nirjala-ekadashi/": "/hi/calendar/festival/nirjala_ekadashi/",
  "/calendar/festival/yogini-ekadashi/": "/calendar/festival/yogini_ekadashi/",
  "/hi/calendar/festival/yogini-ekadashi/": "/hi/calendar/festival/yogini_ekadashi/",
  "/calendar/festival/devshayani-ekadashi/": "/calendar/festival/devshayani_ekadashi/",
  "/hi/calendar/festival/devshayani-ekadashi/": "/hi/calendar/festival/devshayani_ekadashi/",
  "/calendar/festival/kamika-ekadashi/": "/calendar/festival/kamika_ekadashi/",
  "/hi/calendar/festival/kamika-ekadashi/": "/hi/calendar/festival/kamika_ekadashi/",
  "/calendar/festival/shravana-putrada-ekadashi/": "/calendar/festival/shravana_putrada_ekadashi/",
  "/hi/calendar/festival/shravana-putrada-ekadashi/":
    "/hi/calendar/festival/shravana_putrada_ekadashi/",
  "/calendar/festival/aja-ekadashi/": "/calendar/festival/aja_ekadashi/",
  "/hi/calendar/festival/aja-ekadashi/": "/hi/calendar/festival/aja_ekadashi/",
  "/calendar/festival/parsva-ekadashi/": "/calendar/festival/parsva_ekadashi/",
  "/hi/calendar/festival/parsva-ekadashi/": "/hi/calendar/festival/parsva_ekadashi/",
  "/calendar/festival/indira-ekadashi/": "/calendar/festival/indira_ekadashi/",
  "/hi/calendar/festival/indira-ekadashi/": "/hi/calendar/festival/indira_ekadashi/",
  "/calendar/festival/papankusha-ekadashi/": "/calendar/festival/papankusha_ekadashi/",
  "/hi/calendar/festival/papankusha-ekadashi/": "/hi/calendar/festival/papankusha_ekadashi/",
  "/calendar/festival/rama-ekadashi/": "/calendar/festival/rama_ekadashi/",
  "/hi/calendar/festival/rama-ekadashi/": "/hi/calendar/festival/rama_ekadashi/",
  "/calendar/festival/devuthani-ekadashi/": "/calendar/festival/devuthani_ekadashi/",
  "/hi/calendar/festival/devuthani-ekadashi/": "/hi/calendar/festival/devuthani_ekadashi/",
  "/calendar/festival/utpanna-ekadashi/": "/calendar/festival/utpanna_ekadashi/",
  "/hi/calendar/festival/utpanna-ekadashi/": "/hi/calendar/festival/utpanna_ekadashi/",
  "/calendar/festival/mokshada-ekadashi/": "/calendar/festival/mokshada_ekadashi/",
  "/hi/calendar/festival/mokshada-ekadashi/": "/hi/calendar/festival/mokshada_ekadashi/",
  "/calendar/festival/saphala-ekadashi/": "/calendar/festival/saphala_ekadashi/",
  "/hi/calendar/festival/saphala-ekadashi/": "/hi/calendar/festival/saphala_ekadashi/",
  "/calendar/festival/solar-eclipse-feb-2027/": "/calendar/festival/solar_eclipse_feb_2027/",
  "/hi/calendar/festival/solar-eclipse-feb-2027/": "/hi/calendar/festival/solar_eclipse_feb_2027/",
  "/calendar/festival/lunar-eclipse-feb-2027/": "/calendar/festival/lunar_eclipse_feb_2027/",
  "/hi/calendar/festival/lunar-eclipse-feb-2027/": "/hi/calendar/festival/lunar_eclipse_feb_2027/",
  "/calendar/festival/solar-eclipse-aug-2027/": "/calendar/festival/solar_eclipse_aug_2027/",
  "/hi/calendar/festival/solar-eclipse-aug-2027/": "/hi/calendar/festival/solar_eclipse_aug_2027/",
  "/calendar/festival/lunar-eclipse-aug-2027/": "/calendar/festival/lunar_eclipse_aug_2027/",
  "/hi/calendar/festival/lunar-eclipse-aug-2027/": "/hi/calendar/festival/lunar_eclipse_aug_2027/",
  "/calendar/festival/nag-panchami/": "/calendar/festival/nag_panchami/",
  "/hi/calendar/festival/nag-panchami/": "/hi/calendar/festival/nag_panchami/",
  "/calendar/festival/hariyali-teej/": "/calendar/festival/hariyali_teej/",
  "/hi/calendar/festival/hariyali-teej/": "/hi/calendar/festival/hariyali_teej/",
  "/calendar/festival/navami-shraddh/": "/calendar/festival/navami_shraddh/",
  "/hi/calendar/festival/navami-shraddh/": "/hi/calendar/festival/navami_shraddh/",
  "/calendar/festival/maha-navami/": "/calendar/festival/maha_navami/",
  "/hi/calendar/festival/maha-navami/": "/hi/calendar/festival/maha_navami/",
  "/calendar/festival/sharad-purnima/": "/calendar/festival/sharad_purnima/",
  "/hi/calendar/festival/sharad-purnima/": "/hi/calendar/festival/sharad_purnima/",
  "/calendar/festival/tulsi-vivah/": "/calendar/festival/tulsi_vivah/",
  "/hi/calendar/festival/tulsi-vivah/": "/hi/calendar/festival/tulsi_vivah/",
  "/calendar/festival/vaikuntha-ekadashi/": "/calendar/festival/vaikuntha_ekadashi/",
  "/hi/calendar/festival/vaikuntha-ekadashi/": "/hi/calendar/festival/vaikuntha_ekadashi/",
  // END generated festival hyphen spellings.
};
